import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Popover, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Info, Key, ModeEdit, PersonOff, SupervisorAccount, Sync } from "@mui/icons-material";
import { ApiConstant, LangConstant } from "const";
import { useUpdateUser, useResetUserPassword, useRole, useUpdateUserAccount } from "hooks";
import { ChangePasswordDialog } from "../components";
import CopyPasswordDialog from "./CopyPasswordDialog";

const VIEW_MODES = {
  CHANGE_PASSWORD: "changePassword",
  GENERATE_PASSWORD: "generatePassword",
  GRANT_PERMISSION: "grantPermission",
  COPY_PASSWORD: "copyPassword",
};

const UserAction = ({
  data,
  open,
  anchorEl,
  onCloseAction,
  onClickUserInfo,
  onClickUserEdit,
  onOpenPasswordDialog,
}) => {
  const { t: getLabel } = useTranslation();
  const classes = useStyles();
  const { resetUserPassword } = useResetUserPassword();
  const { isServiceAdmin, isRoot } = useRole();
  const { changePermission } = useUpdateUser();
  const { updateUserAccountAsync } = useUpdateUserAccount();

  const [viewMode, setViewMode] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const handleClickChangePassword = () => {
    setViewMode(VIEW_MODES.CHANGE_PASSWORD);
  };

  const handleCloseAllDialogs = () => {
    setViewMode(null);
    onCloseAction();
  };

  const handleResetPassword = () => {
    if (window.confirm(getLabel(LangConstant.TXT_CONFIRM_ACTION))) {
      resetUserPassword({ id: data.id });
      onCloseAction();
    }
  };

  const handleChangePermission = async (isAdmin = true) => {
    if (window.confirm(getLabel(LangConstant.TXT_CONFIRM_ACTION))) {
      const password = await changePermission(isAdmin, data?.accountId);
      if (password) {
        onOpenPasswordDialog(password);
      }
      onCloseAction();
    }
  };

  const handleChangePassword = async payload => {
    const result = await updateUserAccountAsync(payload);

    if (result.status === ApiConstant.STT_OK) {
      setNewPassword(result.data?.pin);
      // setViewMode(VIEW_MODES.COPY_PASSWORD);
    }
  };

  return (
    <Fragment>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onCloseAction}
        classes={{ paper: classes.paper }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box className={classes.wrapAction}>
          {!isServiceAdmin && (
            <Button className={classes.styleButton} onClick={onClickUserInfo}>
              <Info className={classes.actionIcon} />
              <Typography className={classes.actionName}>{getLabel(LangConstant.TXT_INFORMATION)}</Typography>
            </Button>
          )}
          <Button className={classes.styleButton} onClick={onClickUserEdit}>
            <ModeEdit className={classes.actionIcon} />
            <Typography className={classes.actionName}>{getLabel(LangConstant.TXT_EDIT_INFO)}</Typography>
          </Button>
          {isServiceAdmin && (
            <Button className={classes.styleButton} onClick={handleClickChangePassword}>
              <Key className={classes.actionIcon} />
              <Typography className={classes.actionName}>{getLabel(LangConstant.TXT_CHANGE_PASSWORD)}</Typography>
            </Button>
          )}
          {!isServiceAdmin && (
            <Button className={classes.styleButton} onClick={handleResetPassword}>
              <Sync className={classes.actionIcon} />
              <Typography className={classes.actionName}>{getLabel(LangConstant.TXT_RESET_PASSWORD)}</Typography>
            </Button>
          )}
          {isRoot &&
            (Boolean(data && data.isAdmin) ? (
              <Fragment>
                <Button className={classes.styleButton} onClick={() => handleChangePermission(false)}>
                  <PersonOff className={classes.actionIcon} />
                  <Typography className={classes.actionName}>
                    {getLabel(LangConstant.TXT_REMOVE_ADMIN_PERMISSION)}
                  </Typography>
                </Button>
                <Button className={classes.styleButton} onClick={() => handleChangePermission(true)}>
                  <Key className={classes.actionIcon} />
                  <Typography className={classes.actionName}>
                    {getLabel(LangConstant.TXT_GENERATE_NEW_ADMIN_PASSWORD)}
                  </Typography>
                </Button>
              </Fragment>
            ) : (
              <Button className={classes.styleButton} onClick={() => handleChangePermission(true)}>
                <SupervisorAccount className={classes.actionIcon} />
                <Typography className={classes.actionName}>
                  {getLabel(LangConstant.TXT_GRANT_ADMIN_PERMISSION)}
                </Typography>
              </Button>
            ))}
        </Box>
      </Popover>

      {viewMode === VIEW_MODES.CHANGE_PASSWORD && (
        <ChangePasswordDialog open data={data} onClose={handleCloseAllDialogs} onConfirm={handleChangePassword} />
      )}

      {/* {viewMode === VIEW_MODES.COPY_PASSWORD && (
        <CopyPasswordDialog open onClose={() => setViewMode(null)} newPassword={newPassword} />
      )} */}
    </Fragment>
  );
};

export default UserAction;

const useStyles = makeStyles(theme => ({
  paper: {
    marginTop: 0,
    borderRadius: 4,
    overflowX: "unset",
    overflowY: "unset",
    background: "#FFFFFF",
    width: 360,
  },

  actionName: {
    fontSize: 16,
    fontWeight: 500,
    textAlign: "left",
  },

  actionIcon: {
    minWidth: "unset",
    marginRight: 15,
  },

  wrapAction: {
    margin: 10,
  },

  styleButton: {
    color: "gray",
    textTransform: "none",
    width: "100%",
    justifyContent: "left",
  },
}));
